import cogoToast from 'cogo-toast';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { StatusResponse } from '../../../../../../../types/API';
import { wgApi } from '../../../../../../app/src/features/api/wgApi';
import { getUseAdvisoryReportQKey } from '../../../../api/getAdvisoryReport';
import { AdvisoryReport } from '../../../../types/AdvisoryReport';

interface Params {
  reportId: string;
  offers?: string[];
  current_insurances?: string[];
}

export const putInsurancePolicyOrderByReportId = async ({
  reportId,
  offers,
  current_insurances,
}: Params) => {
  const { data, isError } = await wgApi.put<StatusResponse>(
    `v1/api/advisory-reports/${reportId}/insurance-policies/order`,
    { offers, current_insurances },
  );
  if (isError) throw new Error();
  return data;
};

export const useChangeInsurancePolicyOrder = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<StatusResponse, unknown, Params, AdvisoryReport>(
    putInsurancePolicyOrderByReportId,
    {
      onMutate: async ({ reportId, offers, current_insurances }) => {
        const qKey = getUseAdvisoryReportQKey(reportId);
        const prevAdvisoryReport =
          queryClient.getQueryData<AdvisoryReport>(qKey);
        const prevInsurancePolicies = prevAdvisoryReport?.insurance_policies;

        const newCurrentInsurances = current_insurances
          ? prevInsurancePolicies?.current_insurances.sort(
              (a, b) =>
                current_insurances.indexOf(a.id) -
                current_insurances.indexOf(b.id),
            )
          : prevInsurancePolicies?.current_insurances;

        const newOffers = offers
          ? prevInsurancePolicies?.offers.sort(
              (a, b) => offers.indexOf(a.id) - offers.indexOf(b.id),
            )
          : prevInsurancePolicies?.offers;

        await queryClient.cancelQueries(qKey);

        const newInsurancePolicies = {
          ...prevInsurancePolicies,
          offers: newOffers,
          current_insurances: newCurrentInsurances,
        };

        queryClient.setQueryData(qKey, {
          ...prevAdvisoryReport,
          insurance_policies: newInsurancePolicies,
        });

        return prevAdvisoryReport;
      },
      onError: (err, { reportId }, context) => {
        const qKey = getUseAdvisoryReportQKey(reportId);
        cogoToast.error(t('ADVISORY-REPORTS_ADD-CLAUSES_ADD-CLAUSE-FAILED'));
        if (context) queryClient.setQueryData(qKey, context);
      },
    },
  );
};
